/**
 * Ambient league-IP flag + optional contest team-asset folder (DKPLAT-357).
 *
 * `pickem.league_ip` says whether a contest may use licensed (IP) team artwork.
 * With it OFF, NFL/NBA fall back to unlicensed team jerseys under
 * `nfl/jerseys/` / `nba/team-jerseys/` rather than paid logos under `{sport}/teams/`.
 *
 * Contests can also set `style_config.teamAssetFolder` (via Admin's shared
 * AssetFolderSelector). When present, that folder wins over the binary IP
 * default — same picks-core catalog Squares uses (`teams` | `jerseys` |
 * `team-jerseys` | `helmet`).
 *
 * Deliberately mirrors `lowResImages`: set for the *synchronous* duration of a
 * question adapter run (see `QuestionRenderer`) and read by the client-side
 * image chokepoint.
 */

import { getFileExtForSport } from '@low6-pickem/sports-config';

let leagueIpEnabled = false;
let teamAssetFolder: string | null = null;

export function setLeagueIp(enabled: boolean): void {
  leagueIpEnabled = enabled;
}

export function isLeagueIp(): boolean {
  return leagueIpEnabled;
}

/** Contest-level picks-core folder override from `style_config.teamAssetFolder`. */
export function setTeamAssetFolder(folder: string | null | undefined): void {
  teamAssetFolder = folder && folder.trim() ? folder.trim() : null;
}

export function getTeamAssetFolder(): string | null {
  return teamAssetFolder;
}

/**
 * Where each sport keeps its unlicensed team artwork, relative to the sport folder
 * (DKPLAT-357 NFL, DKPLAT-605 NBA).
 */
const NON_IP_TEAM_FOLDER: Record<string, string> = {
  nfl: 'jerseys',
  nba: 'team-jerseys',
  // WNBA reuses the NBA stack, and its artwork follows: generic jerseys keyed by
  // abbreviation under `wnba/team-jerseys/`. There is no `wnba/teams/` — no licensed
  // logo set has been supplied — so this folder is the only artwork it has.
  wnba: 'team-jerseys',
};

export function nonIpTeamFolder(sport: string): string | undefined {
  return NON_IP_TEAM_FOLDER[sport.toLowerCase()];
}

/** Known picks-core team-mark folders in a CDN URL. */
const TEAM_ASSET_RE =
  /^(.*\/)([a-z0-9]+)\/(teams(?:-low)?|jerseys(?:-low)?|team-jerseys(?:-low)?|helmet)\/([^/?#]+)\.[a-z0-9]+([?#].*)?$/i;

const FLAG_ASSET_RE = /^(.*\/)flags\/(round|square)\/([a-z-]+)\.[a-z0-9]+([?#].*)?$/i;
const FLAG_FOLDER_SHAPE: Record<string, 'round' | 'square'> = {
  'flags-round': 'round',
  'flags-square': 'square',
};

/**
 * Remap a known picks-core team asset URL into the contest-selected folder.
 * Returns null when the URL isn't a known team-mark shape.
 */
export function remapUrlToTeamAssetFolder(url: string, folder: string): string | null {
  // Country flags (DKPLAT-606) are keyed by flag code, so round ↔ square is a path swap. A
  // team mark can't become a flag — it carries no country — so any other pairing is left alone.
  const flag = url.match(FLAG_ASSET_RE);
  const flagShape = FLAG_FOLDER_SHAPE[folder.toLowerCase()];
  if (flag || flagShape) {
    if (!flag || !flagShape) return null;
    const [, prefix, , code, suffix = ''] = flag;
    return `${prefix}flags/${flagShape}/${code.toLowerCase()}.png${suffix}`;
  }

  const match = url.match(TEAM_ASSET_RE);
  if (!match) return null;

  const [, prefix, sport, , teamCode, suffix = ''] = match;
  const code = teamCode.toUpperCase();
  const sportLower = sport.toLowerCase();
  const folderLower = folder.toLowerCase();

  if (folderLower === 'helmet') {
    return `${prefix}ncaafb/helmet/${code}.png${suffix}`;
  }
  if (folderLower === 'teams') {
    const ext = getFileExtForSport(sportLower);
    return `${prefix}${sportLower}/teams/${code}.${ext}${suffix}`;
  }
  if (folderLower === 'jerseys' || folderLower === 'team-jerseys') {
    return `${prefix}${sportLower}/${folderLower}/${code}.png${suffix}`;
  }
  return null;
}

const TEAM_LOGO_RE = /^(.*\/)([a-z0-9]+)\/teams\/([^/?#]+)\.[a-z0-9]+([?#].*)?$/i;

/**
 * Resolve a stored team image URL for the active contest settings.
 * Explicit `teamAssetFolder` wins; otherwise League IP off rewrites logos → jerseys.
 */
export function toNonIpTeamImageUrl(url: string): string {
  if (teamAssetFolder) {
    return remapUrlToTeamAssetFolder(url, teamAssetFolder) ?? url;
  }

  if (leagueIpEnabled) return url;

  const match = url.match(TEAM_LOGO_RE);
  if (!match) return url;

  const [, prefix, sport, teamCode, suffix = ''] = match;
  const folder = nonIpTeamFolder(sport);
  if (!folder) return url;

  return `${prefix}${sport.toLowerCase()}/${folder}/${teamCode.toUpperCase()}.png${suffix}`;
}
